// Work Plan API — the day roster planner (postings/gaps/publish/copy) and MC review.
// See healo_backend/ADMIN_API.md § Work plan for the full contract.
import { apiRequest, ApiError, resolveApiUrl } from "@/lib/api/client";
import { getAuthToken } from "@/lib/api/token-store";

function createAuthHeaders(token, headers = {}) {
  const normalizedToken = String(token || getAuthToken() || "").trim();
  if (!normalizedToken) {
    throw new ApiError("authorization token is required", 401);
  }
  return { Authorization: `Bearer ${normalizedToken}`, ...headers };
}

function jsonHeaders(token) {
  return { ...createAuthHeaders(token), "Content-Type": "application/json" };
}

/**
 * The planner bundle for one civil date: sessions, clinics, staff (with per-date MC
 * state), postings, open gaps, read-only doctor sessions and the publish stamp.
 */
export async function fetchWorkPlanDay({ date, token } = {}) {
  const params = new URLSearchParams({ date: String(date || "").trim() });
  const bundle = await apiRequest(`/api/admin/work-plan?${params.toString()}`, {
    headers: createAuthHeaders(token),
    cache: "no-store",
  });
  if (!Array.isArray(bundle?.staff)) return bundle;
  // avatarUrl arrives API-RELATIVE; the renderer is never same-origin with the API, so a
  // plain <img> would resolve it against file:// or the Vite origin and load nothing.
  return {
    ...bundle,
    staff: bundle.staff.map((person) => ({
      ...person,
      avatarUrl: resolveApiUrl(person?.avatarUrl),
    })),
  };
}

/**
 * Post a support staffer into a cell. The server moves an already-booked person instead
 * of double-booking ({moved, fromTenantId}) and resolves an open gap in the cell
 * ({resolvedGapId}). 409 = blocked by a confirmed MC; 422 = tried to post a doctor.
 */
export async function createWorkPlanPosting({ date, tenantId, session, staffId, startTime, endTime, token } = {}) {
  return apiRequest(`/api/admin/work-plan/postings`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify({
      date,
      tenantId,
      session,
      staffId,
      ...(startTime ? { startTime, endTime } : {}),
    }),
  });
}

/** Override a posting's time window (both "HH:MM"), or reset with both empty. */
export async function updateWorkPlanPostingTimes({ postingId, startTime = "", endTime = "", token } = {}) {
  return apiRequest(`/api/admin/work-plan/postings/${encodeURIComponent(postingId)}`, {
    method: "PATCH",
    headers: jsonHeaders(token),
    body: JSON.stringify({ startTime, endTime }),
  });
}

/**
 * Replace a posting's staff flags (ids from the configurable vocabulary — see
 * fetchWorkFlags). The server dedupes, rejects an unknown id with 400 and answers
 * {posting} with the new flagIds; scheduling:manage. 404 on a backend that predates flags.
 */
export async function updateWorkPlanPostingFlags({ postingId, flagIds, token } = {}) {
  return apiRequest(`/api/admin/work-plan/postings/${encodeURIComponent(postingId)}/flags`, {
    method: "PUT",
    headers: jsonHeaders(token),
    body: JSON.stringify({ flagIds: Array.isArray(flagIds) ? flagIds : [] }),
  });
}

export async function deleteWorkPlanPosting({ postingId, token } = {}) {
  await apiRequest(`/api/admin/work-plan/postings/${encodeURIComponent(postingId)}`, {
    method: "DELETE",
    headers: createAuthHeaders(token),
  });
  return true;
}

/**
 * Copy postings from another day (or whole week, weekday-aligned).
 * scope: "day" | "clinic" (tenantId required) | "am" | "pm"; mode: "merge" | "replace".
 * → {copied, replaced, skippedMc, skippedDuplicates, targets}
 */
export async function copyWorkPlan({ fromDate, toDate, scope = "day", tenantId, mode = "merge", weekly = false, token } = {}) {
  return apiRequest(`/api/admin/work-plan/copy`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify({ fromDate, toDate, scope, tenantId, mode, weekly }),
  });
}

/** Publish a day: stamps it visible in the employee app and pushes to posted staff. */
export async function publishWorkPlanDay({ date, token } = {}) {
  return apiRequest(`/api/admin/work-plan/${encodeURIComponent(date)}/publish`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify({}),
  });
}

/** MC review list. status: "pending" | "handled" | "all" (default all). */
export async function fetchMCRequests({ status = "all", token } = {}) {
  const params = new URLSearchParams();
  if (status && status !== "all") {
    params.set("status", status);
  }
  const query = params.toString();
  return apiRequest(`/api/admin/mc${query ? `?${query}` : ""}`, {
    headers: createAuthHeaders(token),
    cache: "no-store",
  });
}

/** Confirm an MC: removes the person's postings over the range, leaves needs-cover gaps. */
export async function confirmMCRequest({ mcId, token } = {}) {
  return apiRequest(`/api/admin/mc/${encodeURIComponent(mcId)}/confirm`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify({}),
  });
}

/** Decline an MC: the person stays on the plan and is notified. */
export async function declineMCRequest({ mcId, token } = {}) {
  return apiRequest(`/api/admin/mc/${encodeURIComponent(mcId)}/decline`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify({}),
  });
}

/**
 * The configurable work-role vocabulary (any active employee). Backed by global settings;
 * the server seeds the defaults on first read. → {roles: [{id, label}]}
 */
export async function fetchWorkRoles({ token } = {}) {
  return apiRequest(`/api/work-roles`, {
    headers: createAuthHeaders(token),
    cache: "no-store",
  });
}

/**
 * Replace the work-role vocabulary (Settings → Work roles, scheduling:manage). Ids are
 * slugs — omit `id` on new rows and the server derives one from the label. [] retires
 * every role.
 */
export async function putWorkRoles({ roles, token } = {}) {
  return apiRequest(`/api/work-roles`, {
    method: "PUT",
    headers: jsonHeaders(token),
    body: JSON.stringify({ roles: Array.isArray(roles) ? roles : [] }),
  });
}

// ---- configurable sessions + staff flags (/api/work-plan/{sessions,flags}) ----
// Both GETs are readable by any active employee and answer {…, canEdit}; both PUTs need
// scheduling:manage. An older backend has neither route (404) — the settings editors show a
// read-only "not supported" state, and the planner falls back to whatever the day bundle says.

function normalizeSessionsPayload(payload) {
  return {
    sessions: Array.isArray(payload?.sessions) ? payload.sessions : [],
    canEdit: Boolean(payload?.canEdit),
    raw: payload,
  };
}

function normalizeFlagsPayload(payload) {
  return {
    flags: Array.isArray(payload?.flags) ? payload.flags : [],
    canEdit: Boolean(payload?.canEdit),
    raw: payload,
  };
}

/**
 * The configured AM/PM sessions (label + hours; keys fixed am/pm). The server seeds the
 * defaults on first read. → {sessions: [{key,label,start,end}], canEdit, raw}
 */
export async function fetchWorkSessions({ token } = {}) {
  const payload = await apiRequest(`/api/work-plan/sessions`, {
    headers: createAuthHeaders(token),
    cache: "no-store",
  });
  return normalizeSessionsPayload(payload);
}

/**
 * Replace the session config (Settings → Work plan). Server validates label 1–12, HH:MM,
 * start<end, am.end<=pm.start (400 with a message otherwise) and broadcasts sessions_changed.
 */
export async function putWorkSessions({ sessions, token } = {}) {
  const payload = await apiRequest(`/api/work-plan/sessions`, {
    method: "PUT",
    headers: jsonHeaders(token),
    body: JSON.stringify({ sessions: Array.isArray(sessions) ? sessions : [] }),
  });
  return normalizeSessionsPayload(payload);
}

/**
 * The staff-flag vocabulary. → {flags: [{id,label,description,icon,colorId}], canEdit, raw}
 */
export async function fetchWorkFlags({ token } = {}) {
  const payload = await apiRequest(`/api/work-plan/flags`, {
    headers: createAuthHeaders(token),
    cache: "no-store",
  });
  return normalizeFlagsPayload(payload);
}

/**
 * Replace the flag vocabulary (Settings → Work plan). Like work roles: omit `id` on new rows
 * and the server derives one from the label, then it is frozen; [] retires every flag.
 * Broadcasts flags_changed.
 */
export async function putWorkFlags({ flags, token } = {}) {
  const payload = await apiRequest(`/api/work-plan/flags`, {
    method: "PUT",
    headers: jsonHeaders(token),
    body: JSON.stringify({ flags: Array.isArray(flags) ? flags : [] }),
  });
  return normalizeFlagsPayload(payload);
}
